/*
 * [CLAIMS_CONTEXT]
 * purpose: Drives the personal property modal flow and step validation.
 * touches: declaration flow, validation
 * invariants: hide product search once a product is selected
 * inputs: personal property change data, step state
 * outputs: updates personal property payloads and validation state
 * see: frontend/src/declaration/store/personalProperty.ts, frontend/src/shared/validator/constraints/personalPropertyConstraints.ts
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { UiButton, UiSpinner, UiCard } from '@claims-ia/design-system';
import PersonalPropertyCategorySelect from '@declaration/components/personalProperty/PersonalPropertyCategorySelect.vue';
import Modal from '@declaration/components/UI/Modal.vue';
import { ClaimTypeFeatureFlag, claimTypeHasFeature } from '@shared/claim/claimTypeFeatureFlags';
import { BDGPersonalPropertyInitialCategorySlug, RepairableStatus, } from '@shared/types/api/personalProperty';
import PersonalPropertyProductSearch from '@declaration/components/personalProperty/v2/steps/PersonalPropertyProductSearch.vue';
import PersonalPropertyInfo from '@declaration/components/personalProperty/v2/steps/PersonalPropertyInfo.vue';
import PersonalPropertyReparations from '@declaration/components/personalProperty/v2/steps/PersonalPropertyReparations.vue';
import { INITIAL_PERSONAL_PROPERTY_CATEGORIES } from '@shared/constants/personalPropertyConstants';
import { PERSONAL_PROPERTY_QUESTION_TRAD_KEYS } from '@declaration/types/personalProperty';
import { idFromIri, optionalResourceIri } from '@shared/utils/iri';
import { InsurerFeatureFlag, insurerHasFeature } from '@shared/featureFlags/insurerFeatureFlags';
const claim = namespace('claim');
const qualification = namespace('qualification');
const personalProperty = namespace('personalProperty');
const settings = namespace('settings');
var PersonalPropertyModalStep;
(function (PersonalPropertyModalStep) {
    PersonalPropertyModalStep[PersonalPropertyModalStep["CategoryChoice"] = 0] = "CategoryChoice";
    PersonalPropertyModalStep[PersonalPropertyModalStep["ProductSearch"] = 1] = "ProductSearch";
    PersonalPropertyModalStep[PersonalPropertyModalStep["Info"] = 2] = "Info";
    PersonalPropertyModalStep[PersonalPropertyModalStep["Reparations"] = 3] = "Reparations";
})(PersonalPropertyModalStep || (PersonalPropertyModalStep = {}));
let PersonalPropertyModal = class PersonalPropertyModal extends Vue {
    constructor() {
        var _a, _b, _c, _d, _e;
        super(...arguments);
        this.currentStep = PersonalPropertyModalStep.CategoryChoice;
        this.searchQuery = '';
        this.isLoading = false;
        this.productSearchHiddenByUser = false;
        this.categoryIri = null;
        this.filteredCategoryProducts = [];
        this.products = [];
        this.bypassPersonalPropertyInfoIfOther = (_e = (_d = (_c = (_b = (_a = this.selfCareSettings) === null || _a === void 0 ? void 0 : _a.declaration) === null || _b === void 0 ? void 0 : _b.pages) === null || _c === void 0 ? void 0 : _c.personalPropertyDamages) === null || _d === void 0 ? void 0 : _d.bypassPersonalPropertyInfoIfOther) !== null && _e !== void 0 ? _e : false;
    }
    get selfCareSettings() {
        if (!this.setting || typeof this.setting !== 'function') {
            return null;
        }
        return this.setting('selfCareSettings');
    }
    get isReparationStepHidden() {
        var _a, _b, _c, _d;
        const list = (_d = (_c = (_b = (_a = this.selfCareSettings) === null || _a === void 0 ? void 0 : _a.declaration) === null || _b === void 0 ? void 0 : _b.pages) === null || _c === void 0 ? void 0 : _c.personalPropertyQuestion) === null || _d === void 0 ? void 0 : _d.hideReparationStep;
        return Array.isArray(list) && list.includes(this.typeTrigram);
    }
    /**
     * Determines if the product search input field should be displayed.
     * Takes into account: current step, category configuration, and user override.
     */
    get shouldShowProductSearchInput() {
        var _a;
        // Must be at ProductSearch step
        if (this.currentStep !== PersonalPropertyModalStep.ProductSearch) {
            return false;
        }
        // User explicitly hid search (clicked "Mon bien n'est pas dans la liste")
        if (this.productSearchHiddenByUser) {
            return false;
        }
        // Check if product search is relevant for current state
        if (!this.shouldSearchProduct) {
            return false;
        }
        // Special case: "autres" category with bypass enabled
        const slug = this.categoryIRI ? (_a = this.categoriesByIri[this.categoryIRI]) === null || _a === void 0 ? void 0 : _a.slug : null;
        if (slug === 'autres' && this.bypassPersonalPropertyInfoIfOther) {
            return false;
        }
        // Check if current guarantee/category allows external catalog search
        if (!this.categoryIRI) {
            return false;
        }
        const categoryHasExternalSearch = !this.categoriesByIri[this.categoryIRI].guaranteeConfigurations.some((config) => config.claimType.trigram === this.typeTrigram && !config.externalApiCatalogResearch);
        return categoryHasExternalSearch;
    }
    get showCategorySearch() {
        if (!this.hasProductSearch) {
            return false;
        }
        if (this.currentStep === PersonalPropertyModalStep.Info) {
            return false;
        }
        return true;
    }
    get searchCategoriesTitle() {
        return this.$tc('claim.damage.personal_property.select.category.title');
    }
    get searchProductsTitle() {
        return this.$tc('claim.damage.personal_property.search.products.title');
    }
    get category() {
        return this.personalProperty.category;
    }
    get categoryIRI() {
        return optionalResourceIri(this.category);
    }
    set categoryIRI(value) {
        if (!value) {
            return;
        }
        const category = value ? this.categoriesByIri[value] : null;
        const payload = {
            category: category === null || category === void 0 ? void 0 : category.id,
            designation: this.designation,
            description: this.description,
        };
        if ((category === null || category === void 0 ? void 0 : category.isAmountOnly) || (category === null || category === void 0 ? void 0 : category.isIdentityDocument)) {
            payload.designation = category.label;
            payload.description = null;
            payload.userKnowPrice = null;
            payload.purchaseCertification = null;
        }
        else if (this.isAmountOnly || this.isIdentityDocument) {
            payload.designation = null;
            payload.receipt = null;
        }
        if (!(category === null || category === void 0 ? void 0 : category.isIdentityDocument)) {
            payload.documentType = null;
        }
        this.update(payload);
    }
    get designation() {
        var _a;
        return (_a = this.personalProperty.designation) !== null && _a !== void 0 ? _a : null;
    }
    set designation(designation) {
        this.update({ designation });
    }
    get description() {
        var _a;
        return (_a = this.personalProperty.description) !== null && _a !== void 0 ? _a : null;
    }
    set description(description) {
        this.update({ description });
    }
    get isAmountOnly() {
        return this.isAmountOnlyGetter(this.changeKey);
    }
    get isIdentityDocument() {
        return this.isIdentityDocumentGetter(this.changeKey);
    }
    get headerTitle() {
        if (this.shouldSelectInitialCategory) {
            return this.$tc('claim.damage.personal_property.modal.new');
        }
        if (this.personalProperty.initialCategorySlug) {
            return this.$tc(INITIAL_PERSONAL_PROPERTY_CATEGORIES[this.personalProperty.initialCategorySlug]
                .labelTradKey);
        }
        return this.$tc(PERSONAL_PROPERTY_QUESTION_TRAD_KEYS[this.typeTrigram].form.title, this.change.data.designation ? 1 : 2, {
            designation: this.change.data.designation,
            number: this.personalPropertyIndex + 1,
        });
    }
    get nextStep() {
        switch (this.currentStep) {
            case PersonalPropertyModalStep.CategoryChoice: {
                return this.shouldSearchProduct
                    ? PersonalPropertyModalStep.ProductSearch
                    : PersonalPropertyModalStep.Info;
            }
            case PersonalPropertyModalStep.ProductSearch: {
                if (this.bypassPersonalPropertyInfoIfOther) {
                    break;
                }
                else {
                    return PersonalPropertyModalStep.Info;
                }
            }
            case PersonalPropertyModalStep.Info: {
                if (this.isReparationStepHidden)
                    return undefined;
                return PersonalPropertyModalStep.Reparations;
            }
            case PersonalPropertyModalStep.Reparations: {
                break;
            }
        }
    }
    get prevStep() {
        switch (this.currentStep) {
            case PersonalPropertyModalStep.CategoryChoice: {
                break;
            }
            case PersonalPropertyModalStep.ProductSearch: {
                if (this.hasInitialCategory)
                    return PersonalPropertyModalStep.CategoryChoice;
                break;
            }
            case PersonalPropertyModalStep.Info: {
                if (this.hasProductSearch)
                    return PersonalPropertyModalStep.ProductSearch;
                else if (this.hasInitialCategory)
                    return PersonalPropertyModalStep.CategoryChoice;
                break;
            }
            case PersonalPropertyModalStep.Reparations: {
                return PersonalPropertyModalStep.Info;
            }
        }
    }
    get currentComponent() {
        switch (this.currentStep) {
            case PersonalPropertyModalStep.CategoryChoice:
                // TODO implémenter le composant pour les cas de figure où une catégorie
                // de départ est disponible (Ex: VOL, BDG)
                return { component: 'div' };
            case PersonalPropertyModalStep.ProductSearch: {
                // Show search component only if input should be displayed
                if (!this.shouldShowProductSearchInput) {
                    return { component: 'div' };
                }
                return {
                    component: PersonalPropertyProductSearch,
                    props: { value: this.searchQuery, changeKey: this.changeKey },
                    events: {
                        input: (input) => (this.searchQuery = input),
                        selected: this.onProductSelected,
                        missing: this.onProductMissing,
                    },
                };
            }
            case PersonalPropertyModalStep.Info:
                return {
                    component: PersonalPropertyInfo,
                    props: { changeKey: this.changeKey },
                };
            case PersonalPropertyModalStep.Reparations:
                return {
                    component: PersonalPropertyReparations,
                    props: { changeKey: this.changeKey },
                };
        }
    }
    get isPurchaseInvoiceMandatory() {
        return this.isPurchaseInvoiceMandatoryGetter(this.changeKey);
    }
    get canValidate() {
        switch (this.currentStep) {
            case PersonalPropertyModalStep.CategoryChoice:
            case PersonalPropertyModalStep.ProductSearch:
                return true;
            case PersonalPropertyModalStep.Info: {
                const invalidFields = [
                    'category',
                    'documentType',
                    'designation',
                    'description',
                    'userKnowPrice',
                    'age',
                    'amount',
                    'purchaseCertification',
                    'receipt',
                ].filter((fieldName) => this.validation.fieldHasViolations(fieldName));
                // If invoice is mandatory, it must be provided
                if (this.isPurchaseInvoiceMandatory && !this.personalProperty.receipt) {
                    return false;
                }
                return invalidFields.length === 0;
            }
            case PersonalPropertyModalStep.Reparations: {
                return this.isRepairStepValid();
            }
        }
    }
    /**
     * Validates the repair step based on feature flags and repairable status.
     * Three valid scenarios:
     * 1. justificativesNotRequired === true (no repair files required)
     * 2. Repairable item with repair quote file collection not empty
     * 3. Irreparable item with repair files (irreparability certificate)
     */
    isRepairStepValid() {
        var _a;
        const personalProperty = this.personalProperty;
        const validation = this.validation;
        const repairFileCollection = personalProperty.repairFileCollection;
        // Case 1: Feature flag disables repair justificatives
        if (this.justificativesNotRequired) {
            return true;
        }
        // Case 2: Repairable property with repair file + amount
        const isRepairable = personalProperty.repairableStatus === RepairableStatus.Repairable;
        const hasRepairFile = (repairFileCollection === null || repairFileCollection === void 0 ? void 0 : repairFileCollection.uploadedAppFiles) && repairFileCollection.uploadedAppFiles.length > 0;
        const hasRepairAmount = ((_a = personalProperty.repairQuoteInfo) === null || _a === void 0 ? void 0 : _a.amount) && personalProperty.repairQuoteInfo.amount > 0;
        const noFileViolations = !validation.fieldHasViolations('repairFileCollection');
        const noAmountViolations = !validation.fieldHasViolations('repairQuoteInfo.amount');
        if (isRepairable &&
            hasRepairFile &&
            hasRepairAmount &&
            noFileViolations &&
            noAmountViolations) {
            return true;
        }
        // Case 3: Irreparable property with irreparability certificate
        const isIrrepairable = personalProperty.repairableStatus === RepairableStatus.Irreparable;
        const hasIrreparabilityCertificate = (repairFileCollection === null || repairFileCollection === void 0 ? void 0 : repairFileCollection.uploadedAppFiles) && repairFileCollection.uploadedAppFiles.length > 0;
        if (isIrrepairable && hasIrreparabilityCertificate && noFileViolations) {
            return true;
        }
        return false;
    }
    get nextLabel() {
        if (this.nextStep)
            return this.$tc('claim.button.next-step');
        return this.$tc('claim.button.confirm');
    }
    get change() {
        return this.changeGetter(this.changeKey);
    }
    get validation() {
        return this.validationGetter(this.changeKey);
    }
    get justificativesNotRequired() {
        return insurerHasFeature(InsurerFeatureFlag.DisableDamagesJustificatives);
    }
    get personalProperty() {
        return this.change.data;
    }
    get hasInitialCategory() {
        return claimTypeHasFeature(this.typeTrigram, ClaimTypeFeatureFlag.InitialPersonalPropertyCategoryChoice);
    }
    get hasProductSearch() {
        return (!this.personalProperty.initialCategorySlug ||
            INITIAL_PERSONAL_PROPERTY_CATEGORIES[this.personalProperty.initialCategorySlug]
                .isProductSearchAvailable);
    }
    // Unused
    get hasReparations() {
        return (claimTypeHasFeature(this.typeTrigram, ClaimTypeFeatureFlag.PersonalPropertyReparationForm) &&
            ![
                BDGPersonalPropertyInitialCategorySlug.ElectromenagerOuMultimedia,
                BDGPersonalPropertyInitialCategorySlug.Autres,
            ].includes(this.personalProperty.initialCategorySlug));
    }
    get shouldSelectInitialCategory() {
        return this.hasInitialCategory && !this.personalProperty.initialCategorySlug;
    }
    get shouldSearchProduct() {
        return (this.hasProductSearch &&
            this.personalProperty.thirdPartyProductId === null &&
            this.change.data.designation === null);
    }
    get isCategoryModifiable() {
        return this.isCategoryModifiableGetter(this.changeKey);
    }
    async fetchRepairQuoteMandatory() {
        var _a, _b, _c;
        try {
            this.isLoading = true;
            const age = (_a = this.personalProperty.age) !== null && _a !== void 0 ? _a : null;
            const valuation = ((_b = this.personalProperty.thirdPartyProductInfo) === null || _b === void 0 ? void 0 : _b.amount)
                ? this.personalProperty.thirdPartyProductInfo.amount * 100
                : this.personalProperty.amount;
            const discountApplied = !!((_c = this.personalProperty.thirdPartyProductInfo) === null || _c === void 0 ? void 0 : _c.amount);
            const personalPropertyCategoryIri = optionalResourceIri(this.personalProperty.category);
            const personalPropertyCategoryId = personalPropertyCategoryIri
                ? idFromIri(personalPropertyCategoryIri)
                : null;
            const isRepairQuoteMandatory = await this.$axios.$post('/personal-property/is-repair-quote-mandatory', {
                age,
                valuation,
                personalPropertyCategoryId,
                discountApplied,
            });
            this.updatePersonalProperty({ changeKey: this.changeKey, data: { isRepairQuoteMandatory } });
        }
        catch (e) {
            // Error handled silently
        }
        finally {
            this.isLoading = false;
        }
    }
    async save() {
        try {
            this.isLoading = true;
            await this.savePersonalProperty(this.saveStep ? { step: this.saveStep } : undefined);
        }
        catch (e) {
            // Error handled silently
        }
        finally {
            this.isLoading = false;
        }
    }
    async next() {
        // Skip to Info if search input is not shown and we're before Info step
        if (!this.shouldShowProductSearchInput && this.currentStep < PersonalPropertyModalStep.Info) {
            this.currentStep = PersonalPropertyModalStep.Info;
            return;
        }
        if (this.currentStep === PersonalPropertyModalStep.Reparations) {
            await this.save();
            this.close();
            return;
        }
        const nextStep = this.nextStep;
        if (!nextStep) {
            await this.save();
            this.close();
            return;
        }
        if (nextStep === PersonalPropertyModalStep.ProductSearch) {
            this.searchQuery = '';
        }
        this.currentStep = nextStep;
        if (nextStep === PersonalPropertyModalStep.Info) {
            await this.fetchRepairQuoteMandatory();
        }
    }
    fieldType(key) {
        var _a;
        return ((_a = this.validation.validator) === null || _a === void 0 ? void 0 : _a.revealViolations) && !this.validation.fieldIsValid(key)
            ? 'is-danger'
            : null;
    }
    fieldMessage(key) {
        var _a;
        return ((_a = this.validation.validator) === null || _a === void 0 ? void 0 : _a.revealViolations)
            ? this.validation.fieldErrors(key, this)[0]
            : null;
    }
    goBack() {
        if (!this.prevStep)
            return;
        if (this.prevStep === PersonalPropertyModalStep.ProductSearch) {
            this.updatePersonalProperty({
                changeKey: this.changeKey,
                data: { thirdPartyProductId: null, thirdPartyProductInfo: null, designation: null },
            });
        }
        this.currentStep = this.prevStep;
    }
    cancel() {
        this.resetPersonalProperty();
        this.$emit('close');
        this.searchQuery = '';
        this.productSearchHiddenByUser = false;
    }
    close() {
        this.$emit('close');
    }
    onProductSelected({ product, createdProduct, }) {
        this.updatePersonalProperty({
            changeKey: this.changeKey,
            data: {
                description: product.brand,
                designation: product.name,
                category: createdProduct.personalPropertyCategory,
                thirdPartyProductId: createdProduct.itemId,
                thirdPartyProductInfo: { ...product, ...createdProduct },
                userKnowPrice: true,
                age: null,
                amount: product.amount * 100,
                purchaseCertification: false,
                repairableStatus: null,
                repairQuoteInfo: { amount: null, companyName: null, companyPhoneNumber: null },
            },
        });
        this.currentStep = PersonalPropertyModalStep.Info;
    }
    onProductMissing(productName) {
        const payload = {
            designation: productName,
            description: null,
            userKnowPrice: true,
            age: null,
            amount: null,
            purchaseCertification: false,
            repairableStatus: RepairableStatus.Repairable,
            repairQuoteInfo: { amount: null, companyName: null, companyPhoneNumber: null },
        };
        if (this.categoryIRI) {
            const category = this.categoriesByIri[this.categoryIRI];
            if ((category === null || category === void 0 ? void 0 : category.isAmountOnly) || (category === null || category === void 0 ? void 0 : category.isIdentityDocument)) {
                payload.designation = category.label;
                payload.description = null;
                payload.userKnowPrice = null;
                payload.purchaseCertification = null;
            }
        }
        if (this.isCategoryModifiable) {
            payload.category = this.categoryIRI;
        }
        this.updatePersonalProperty({
            changeKey: this.changeKey,
            data: payload,
        });
        // Hide search input since user couldn't find their product
        this.productSearchHiddenByUser = true;
        this.currentStep = PersonalPropertyModalStep.Info;
    }
    revealValidation() {
        this.$store.dispatch('error/add', this.$tc('error.form.please_validate_message'));
        this.validatePersonalProperty(this.changeKey);
    }
    update(data) {
        this.updatePersonalProperty({ changeKey: this.changeKey, data });
    }
};
__decorate([
    claim.State('claim')
], PersonalPropertyModal.prototype, "claim", void 0);
__decorate([
    qualification.State('typeTrigram')
], PersonalPropertyModal.prototype, "typeTrigram", void 0);
__decorate([
    personalProperty.State('categoriesByIri')
], PersonalPropertyModal.prototype, "categoriesByIri", void 0);
__decorate([
    personalProperty.Getter('change')
], PersonalPropertyModal.prototype, "changeGetter", void 0);
__decorate([
    personalProperty.Getter('validation')
], PersonalPropertyModal.prototype, "validationGetter", void 0);
__decorate([
    personalProperty.Getter('isCategoryModifiable')
], PersonalPropertyModal.prototype, "isCategoryModifiableGetter", void 0);
__decorate([
    personalProperty.Getter('isAmountOnly')
], PersonalPropertyModal.prototype, "isAmountOnlyGetter", void 0);
__decorate([
    personalProperty.Getter('isIdentityDocument')
], PersonalPropertyModal.prototype, "isIdentityDocumentGetter", void 0);
__decorate([
    personalProperty.Action('updateDamage')
], PersonalPropertyModal.prototype, "updatePersonalProperty", void 0);
__decorate([
    personalProperty.Action('validateAndRevealDamages')
], PersonalPropertyModal.prototype, "validatePersonalProperty", void 0);
__decorate([
    personalProperty.Action('resetChanges')
], PersonalPropertyModal.prototype, "resetPersonalProperty", void 0);
__decorate([
    personalProperty.Action('saveChanges')
], PersonalPropertyModal.prototype, "savePersonalProperty", void 0);
__decorate([
    settings.Getter('setting')
], PersonalPropertyModal.prototype, "setting", void 0);
__decorate([
    personalProperty.Getter('isPurchaseInvoiceMandatory')
], PersonalPropertyModal.prototype, "isPurchaseInvoiceMandatoryGetter", void 0);
PersonalPropertyModal = __decorate([
    Component({
        components: {
            Modal,
            UiButton,
            PersonalPropertyCategorySelect,
            PersonalPropertyProductSearch,
            PersonalPropertyInfo,
            PersonalPropertyReparations,
            UiSpinner,
            UiCard,
        },
        props: {
            changeKey: { type: [Number, String], default: null },
            personalPropertyIndex: { type: Number, default: 0 },
            isDisplayed: { type: Boolean, required: true },
            confirmDelete: { type: Function, required: true },
            saveStep: { type: String, default: null },
        },
        watch: {
            isDisplayed(isDisplayed) {
                if (!isDisplayed) {
                    this.searchQuery = '';
                    this.categoryIRI = '';
                    this.productSearchHiddenByUser = false;
                }
                else if (this.shouldSelectInitialCategory) {
                    this.currentStep = PersonalPropertyModalStep.CategoryChoice;
                }
                else if (this.shouldSearchProduct) {
                    this.currentStep = PersonalPropertyModalStep.ProductSearch;
                }
                else {
                    this.currentStep = PersonalPropertyModalStep.Info;
                }
            },
        },
    })
], PersonalPropertyModal);
export default PersonalPropertyModal;
